import {
  Button,
  ButtonGroup,
  FlexBox,
  Loader,
  space,
  Typography,
} from "@waldner-laboreinrichtungen/component-system";
import type { FC } from "react";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import useSWR from "swr";

import type {
  UAAnalogControlFunction,
  UADiscreteControlFunction,
  UAFunction,
} from "../../server/lads-api/clients/lcc-openapi";
import {
  receiveLADSFunctionalUnits,
  receiveLADSFunctionSet,
} from "../api/lads";
import { useAccessToken } from "../auth-provider";
import type { Device } from "../view-model";

export type LADSDeviceControlProps = {
  device: Device;
};

type ControlFunction = {
  browseName: string;
  displayName: string;
  unitId: string;
  unitDisplayName: string;
  functionPath: string;
  controlType: "discrete" | "analog";
  enumStrings?: string[]; // For discrete controls (On/Off, etc.)
  unit?: string; // For analog controls (°C, etc.)
  euRange?: { low: number; high: number }; // For analog controls
  currentValue?: string | number;
  targetValue?: string | number;
  isUserWritable: boolean;
  isEnabled: boolean;
};

function extractControlFunctions(
  functionalUnits: Array<{
    browseName?: string;
    displayName?: string;
    functionSet?: unknown[];
  }>,
  functionSetsMap: Map<string, UAFunction[]>,
  deviceId: string
): ControlFunction[] {
  const controlFunctions: ControlFunction[] = [];

  for (const unit of functionalUnits) {
    const unitId = unit.browseName || "";
    // Check if functionSet is directly in the unit, otherwise use the map
    // The API response includes functionSet even though TypeScript type doesn't
    const functionSets: UAFunction[] =
      unit.functionSet &&
      Array.isArray(unit.functionSet) &&
      unit.functionSet.length > 0
        ? (unit.functionSet as UAFunction[])
        : functionSetsMap.get(unitId) || [];

    for (const func of functionSets) {
      // Must be a control function (type includes "Control")
      if (!func.typeHierarchy?.includes("BaseControlFunctionType")) continue;
      // Determine control type: discrete (has enumStrings) or analog (has euRange)
      const isDiscrete = func.typeHierarchy?.includes(
        "DiscreteControlFunctionType"
      );

      const controlFunc: UAAnalogControlFunction | UADiscreteControlFunction =
        func;
      const targetValue = controlFunc.targetValue;
      if (!targetValue) continue;

      const functionPath = `DeviceSet/${deviceId}/FunctionalUnitSet/${unitId}/FunctionSet/${controlFunc.browseName}`;
      const currentValue = controlFunc.currentValue?.value?.value;
      const targetValueValue = targetValue.value?.value;
      // Cast to access properties not in strict type definition
      const targetValueAny = targetValue as Record<string, unknown>;

      controlFunctions.push({
        browseName: controlFunc.browseName || "",
        displayName: controlFunc.displayName || controlFunc.browseName || "",
        unitId,
        unitDisplayName: unit.displayName || unitId,
        functionPath,
        controlType: isDiscrete ? "discrete" : "analog",
        enumStrings: isDiscrete ? targetValue.enumStrings || [] : undefined,
        unit: targetValueAny.engineeringUnits as string | undefined,
        euRange: targetValueAny.euRange as
          | { low: number; high: number }
          | undefined,
        currentValue: currentValue,
        targetValue: targetValueValue,
        isUserWritable: true, // TODO: Check accessLevel when available in API
        isEnabled: controlFunc.isEnabled ?? true,
      });
    }
  }

  return controlFunctions;
}

async function setFunctionTargetValue(
  _functionPath: string,
  _value: string,
  _accessToken?: string
): Promise<void> {
  // TODO: Implement API call to set target value
  // This might require a PUT/PATCH endpoint that doesn't exist yet
  // Placeholder for actual API call
  // await ladsClient.setFunctionTargetValue({ functionPath, value });
}

export const LADSDeviceControl: FC<LADSDeviceControlProps> = ({ device }) => {
  const { t } = useTranslation();
  const accessToken = useAccessToken();
  const [functionSetsMap, setFunctionSetsMap] = useState<
    Map<string, UAFunction[]>
  >(new Map());
  const [isLoadingFunctionSets, setIsLoadingFunctionSets] = useState(false);

  // Fetch functional units - functionSet is now included in the OpenAPI spec
  const {
    data: functionalUnits,
    error: functionalUnitsError,
    isLoading: isLoadingUnits,
  } = useSWR(
    device.id ? [`functionalUnits`, device.id] : null,
    ([, deviceId]) =>
      receiveLADSFunctionalUnits(deviceId, accessToken ?? undefined),
    {
      revalidateOnFocus: false,
      refreshInterval: 30_000, // Refresh every 30 seconds
    }
  );

  useEffect(() => {
    if (!functionalUnits || functionalUnits.length === 0) {
      setFunctionSetsMap(new Map());
      setIsLoadingFunctionSets(false);
      return;
    }

    const fetchFunctionSets = async () => {
      setIsLoadingFunctionSets(true);
      const newMap = new Map<string, UAFunction[]>();
      await Promise.all(
        functionalUnits.map(async (unit) => {
          const unitId = unit.browseName || "";
          if (!unitId) return;

          // First check if functionSet is already in the list response (even though TypeScript doesn't know about it)
          if (
            "functionSet" in unit &&
            Array.isArray(unit.functionSet) &&
            unit.functionSet.length > 0
          ) {
            newMap.set(unitId, unit.functionSet as UAFunction[]);
            return;
          }

          try {
            const { receiveLADSFunctionalUnit } = await import("../api/lads");
            const fullUnit = await receiveLADSFunctionalUnit(
              device.id,
              unitId,
              accessToken ?? undefined
            );
            if (
              "functionSet" in fullUnit &&
              Array.isArray(fullUnit.functionSet) &&
              fullUnit.functionSet.length > 0
            ) {
              newMap.set(unitId, fullUnit.functionSet as UAFunction[]);
            } else {
              // Fallback: fetch function sets via separate endpoint
              const functionSets = await receiveLADSFunctionSet(
                device.id,
                unitId,
                accessToken ?? undefined
              );
              newMap.set(unitId, functionSets as UAFunction[]);
            }
          } catch {
            // Failed to fetch function set for this unit
            newMap.set(unitId, []);
          }
        })
      );
      setFunctionSetsMap(newMap);
      setIsLoadingFunctionSets(false);
    };

    fetchFunctionSets();
  }, [functionalUnits, device.id, accessToken]);

  const controlFunctions = useMemo(() => {
    if (!functionalUnits) return [];
    // For now, only show discrete controls (buttons) — analog controls are hidden for the trade show
    return extractControlFunctions(
      functionalUnits,
      functionSetsMap,
      device.id
    ).filter((cf) => cf.controlType === "discrete");
  }, [functionalUnits, functionSetsMap, device.id]);

  const handleControlClick = async (
    controlFunction: ControlFunction,
    value: string
  ) => {
    if (!controlFunction.isUserWritable) return;

    try {
      await setFunctionTargetValue(
        controlFunction.functionPath,
        value,
        accessToken ?? undefined
      );
      // TODO: Optimistically update the UI or refetch function sets
    } catch {
      // Failed to set target value
    }
  };

  if (isLoadingUnits || isLoadingFunctionSets) {
    return (
      <FlexBox alignItems="center" gap={space(2)}>
        <Loader size="24px" />
        <Typography>{t("detail.control.loading")}</Typography>
      </FlexBox>
    );
  }

  if (functionalUnitsError) {
    return (
      <Typography color="grey400">
        {t("detail.control.error") || "Failed to load control functions"}
      </Typography>
    );
  }

  if (controlFunctions.length === 0) {
    return (
      <Typography color="grey400">
        {t("detail.control.noControls") || "No control functions available"}
      </Typography>
    );
  }

  return (
    <FlexBox flexDirection="column" gap={space(6)}>
      {controlFunctions.map((controlFunction) => (
        <FlexBox flexDirection="column" key={controlFunction.functionPath}>
          <Typography block marginBottom={space(3)} weight="semiBold">
            {controlFunction.displayName}
            {controlFunction.unitDisplayName !== controlFunction.displayName &&
              ` (${controlFunction.unitDisplayName})`}
          </Typography>

          {/* Discrete Control: Show buttons for each enum value */}
          {controlFunction.controlType === "discrete" &&
            controlFunction.enumStrings && (
              <ButtonGroup>
                {controlFunction.enumStrings.map((enumValue) => {
                  const isActive =
                    controlFunction.currentValue === enumValue ||
                    controlFunction.targetValue === enumValue;
                  // TODO: re-enable when write-to-device API is implemented
                  // const isDisabled =
                  //   !controlFunction.isUserWritable ||
                  //   !controlFunction.isEnabled ||
                  //   device.state?.status === "offline" ||
                  //   device.state?.status === "error";
                  const isDisabled = true;

                  return (
                    <Button
                      disabled={isDisabled}
                      key={`${controlFunction.functionPath}-${enumValue}`}
                      onClick={() =>
                        handleControlClick(controlFunction, enumValue)
                      }
                      variant={isActive ? "primary" : "secondary"}
                    >
                      {t(`control.value.${enumValue}`, enumValue)}
                    </Button>
                  );
                })}
              </ButtonGroup>
            )}

          {/* Analog Control: Input field for setting target value
          {controlFunction.controlType === "analog" && (
            <FlexBox alignItems="center" gap={space(4)}>
              <input
                disabled={
                  !controlFunction.isUserWritable ||
                  !controlFunction.isEnabled ||
                  device.state?.status === "offline" ||
                  device.state?.status === "error"
                }
                max={controlFunction.euRange?.high}
                min={controlFunction.euRange?.low}
                onChange={(e) =>
                  handleControlClick(controlFunction, e.target.value)
                }
                placeholder={String(controlFunction.targetValue ?? "")}
                step="0.1"
                type="number"
              />
              {controlFunction.unit && (
                <Typography>{controlFunction.unit}</Typography>
              )}
              {controlFunction.euRange && (
                <Typography color="grey400" size="sm">
                  ({controlFunction.euRange.low} -{" "}
                  {controlFunction.euRange.high})
                </Typography>
              )}
            </FlexBox>
          )} */}
        </FlexBox>
      ))}
    </FlexBox>
  );
};
